/**
 * Utility functions for seminar score calculation and related operations
 */

/**
 * Calculate the final score for a seminar based on evaluator scores and standardized weights
 * @param {Object} seminar - The seminar object containing nilai (scores) and other data
 * @param {Object} tugasAkhir - Optional tugasAkhir object for role identification
 * @returns {Object} An object containing finalScore, evaluatorScores, and isPassed flag
 */
export const calculateSeminarScore = (seminar, tugasAkhir = null) => {
  if (!seminar || !seminar.nilai) {
    return {
      finalScore: 0,
      evaluatorScores: [],
      isPassed: false
    };
  }

  // Get tugas akhir data from seminar if not provided
  if (!tugasAkhir && seminar.tugasAkhir) {
    tugasAkhir = seminar.tugasAkhir;
  }

  // Extract pembimbing IDs for role identification
  const pembimbingIds = [];
  if (tugasAkhir && Array.isArray(tugasAkhir.pembimbingId)) {
    pembimbingIds.push(...tugasAkhir.pembimbingId.filter(id => typeof id === 'string'));
  }

  // Track assigned roles
  const assignedRoles = {
    'Pembimbing 1': false,
    'Pembimbing 2': false,
    'Penguji 1': false,
    'Penguji 2': false
  };

  // Get all evaluator IDs
  const pengujiIds = Object.keys(seminar.nilai);

  // First assign pembimbing roles
  const evaluatorScores = [];
  pengujiIds.forEach(pengujiId => {
    const pembimbingIndex = pembimbingIds.indexOf(pengujiId);

    if (pembimbingIndex === 0) {
      evaluatorScores.push({
        id: pengujiId,
        role: 'Pembimbing 1',
        weight: 0.35
      });
      assignedRoles['Pembimbing 1'] = true;
    } else if (pembimbingIndex === 1) {
      evaluatorScores.push({
        id: pengujiId,
        role: 'Pembimbing 2',
        weight: 0.25
      });
      assignedRoles['Pembimbing 2'] = true;
    }
  });

  // Then assign remaining penguji roles
  pengujiIds.forEach(pengujiId => {
    // Skip if already assigned as pembimbing
    if (evaluatorScores.some(e => e.id === pengujiId)) return;

    // Assign as penguji 1 or 2
    if (!assignedRoles['Penguji 1']) {
      evaluatorScores.push({
        id: pengujiId,
        role: 'Penguji 1',
        weight: 0.20
      });
      assignedRoles['Penguji 1'] = true;
    } else if (!assignedRoles['Penguji 2']) {
      evaluatorScores.push({
        id: pengujiId,
        role: 'Penguji 2',
        weight: 0.20
      });
      assignedRoles['Penguji 2'] = true;
    } else {
      // If we have extra evaluators, add default weight
      evaluatorScores.push({
        id: pengujiId,
        role: `Penguji Extra`,
        weight: 0.20
      });
    }
  });

  // Calculate individual scores for each evaluator
  let totalScore = 0;
  let totalWeight = 0;

  // Process each evaluator
  evaluatorScores.forEach(evaluator => {
    const pengujiNilai = seminar.nilai[evaluator.id];
    if (!pengujiNilai) {
      evaluator.score = 0;
      return;
    }

    // Calculate score for this evaluator
    let total = 0;
    let count = 0;
    const criteriaScores = [];

    // Process all criteria
    if (Array.isArray(seminar.kriteriaPenilaian)) {
      seminar.kriteriaPenilaian.forEach(kriteria => {
        if (kriteria && kriteria.id && pengujiNilai[kriteria.id]) {
          const nilai = pengujiNilai[kriteria.id];
          const bobot = kriteria.bobot / 100;
          const weightedScore = nilai * bobot;

          total += weightedScore;
          count++;

          criteriaScores.push({
            id: kriteria.id,
            name: kriteria.nama,
            score: nilai,
            weight: kriteria.bobot,
            weighted: weightedScore
          });
        }
      });
    }

    // Save the score
    const score = count > 0 ? total : 0;
    evaluator.score = score;
    evaluator.criteriaScores = criteriaScores;

    // Add to total
    totalScore += score * evaluator.weight;
    totalWeight += evaluator.weight;
  });

  // Normalize if not all evaluators submitted scores
  if (totalWeight > 0 && totalWeight < 1) {
    totalScore = totalScore / totalWeight;
  }

  // Handle test case: P1=40, P2=50, Penguji1=60, Penguji2=70
  // Should yield 52.5 = (40*0.35 + 50*0.25 + 60*0.20 + 70*0.20)

  // Return the result
  return {
    finalScore: totalScore,
    evaluatorScores: evaluatorScores,
    isPassed: totalScore >= 60,
    totalWeight: totalWeight
  };
};

/**
 * Gets evaluator name from seminar data based on ID
 * @param {Object} seminar - The seminar object
 * @param {string} pengujiId - The evaluator ID to look up
 * @param {string} defaultName - Default name if not found
 * @returns {string} The evaluator name
 */
export const getEvaluatorName = (seminar, pengujiId, defaultName = "Penguji") => {
  if (!seminar || !seminar.pengujiId || !seminar.pengujiNama) {
    return defaultName;
  }

  const pengujiIndex = seminar.pengujiId.indexOf(pengujiId);
  if (pengujiIndex >= 0 && pengujiIndex < seminar.pengujiNama.length) {
    return seminar.pengujiNama[pengujiIndex];
  }

  return defaultName;
};

/**
 * Count valid bimbingan sessions with proper filtering
 * @param {Array} bimbinganData - Array of bimbingan objects
 * @returns {Object} Counts of different types of bimbingan
 */
export const countBimbinganSessions = (bimbinganData) => {
  if (!Array.isArray(bimbinganData)) {
    return {
      total: 0,
      reviewed: 0,
      pending: 0,
      proposal: 0,
      hasil: 0,
      finalisasi: 0
    };
  }

  // Filter out deleted sessions and introduction sessions (sesi 0)
  // Introduction sessions have isIntroduction: true or sesi === 0 and should not be counted
  const validSessions = bimbinganData.filter(b =>
    b.deleted !== true &&
    b.isIntroduction !== true &&
    b.sesi !== 0
  );

  // Count by status
  const reviewed = validSessions.filter(b => b.status === 'reviewed');
  const pending = validSessions.filter(b => b.status === 'pending');

  // Count by type (only for reviewed)
  const proposal = reviewed.filter(b => !b.jenisBimbingan || b.jenisBimbingan === 'proposal').length;
  const hasil = reviewed.filter(b => b.jenisBimbingan === 'hasil').length;
  const finalisasi = reviewed.filter(b => b.jenisBimbingan === 'finalisasi').length;

  return {
    total: validSessions.length,
    reviewed: reviewed.length,
    pending: pending.length,
    proposal,
    hasil,
    finalisasi
  };
};